/**
 * Back in stock "Notify me" trigger. Opens the published Klaviyo form-builder
 * form (id from settings.klaviyo_bis_form_id) rendered by
 * snippets/klaviyo-back-in-stock.liquid.
 *
 * This lives in assets/ rather than in the snippet's {% javascript %} tag on
 * purpose. Shopify only includes a snippet's {% javascript %} in the page
 * bundle when that snippet renders during the initial server render, and the
 * Section Rendering API response for a variant change adds no new JS to the
 * page. So when a shopper landed on an available variant and then selected a
 * sold-out one, <klaviyo-back-in-stock> was injected into a page where this
 * custom element had never been defined: it never upgraded, no click listener
 * was bound, and the button silently did nothing until a page refresh.
 * Loaded from layout/theme.liquid, it is always defined.
 *
 * Klaviyo's onsite bundle also loads async and then fetches its form
 * definitions, and an `openForm` pushed before that is ready is dropped without
 * any error, so the trigger re-pushes until Klaviyo confirms the form opened.
 */

const KLAVIYO_BIS_RETRY_INTERVAL = 300;
const KLAVIYO_BIS_RETRY_TIMEOUT = 8000;

if (!customElements.get('klaviyo-back-in-stock')) {
  customElements.define(
    'klaviyo-back-in-stock',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.klaviyo-bis__trigger');

        if (!this.button) {
          return;
        }

        this.abortController = new AbortController();

        this.button.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });

        // Klaviyo dispatches this on window for every form lifecycle event.
        window.addEventListener('klaviyoForms', this.onKlaviyoFormEvent.bind(this), {
          signal: this.abortController.signal,
        });
      }

      disconnectedCallback() {
        // The theme swaps this element out server-side on every variant change.
        this.abortController?.abort();
        this.stopWaiting();
      }

      get formId() {
        return this.dataset.klaviyoFormId;
      }

      onClick(event) {
        event.preventDefault();

        if (!this.formId || this.waiting) {
          return;
        }

        this.syncVariant();

        this.waiting = true;
        this.button.setAttribute('aria-busy', 'true');
        this.openForm(Date.now());
      }

      openForm(startedAt) {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', this.formId]);

        if (Date.now() - startedAt >= KLAVIYO_BIS_RETRY_TIMEOUT) {
          this.stopWaiting();
          return;
        }

        this.retryTimeout = setTimeout(
          () => this.openForm(startedAt),
          KLAVIYO_BIS_RETRY_INTERVAL
        );
      }

      onKlaviyoFormEvent(event) {
        if (event.detail?.formId === this.formId && event.detail?.type === 'open') {
          this.stopWaiting();
        }
      }

      stopWaiting() {
        clearTimeout(this.retryTimeout);
        this.waiting = false;
        this.button?.removeAttribute('aria-busy');
      }

      /**
       * The Klaviyo form subscribes the shopper to the variant in the URL's
       * ?variant= param, so sync it from the product form's selected variant
       * before opening, otherwise they subscribe to the wrong size.
       */
      syncVariant() {
        const productFormId = this.dataset.productFormId;
        let form = null;

        if (productFormId) {
          form = document.getElementById(productFormId);
        }

        if (!form) {
          form = this.closest('form');
        }

        const input = form
          ? form.querySelector('input[name="id"]')
          : document.querySelector('input[name="id"]');

        if (!input || !input.value) {
          return;
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState(window.history.state, '', url);
      }
    }
  );
}
